import {
	dxtToLocalServerTime,
	ErrorModal,
	HelmetDexteel,
	useShiftNavigator,
} from "@dexteel/mesf-core";
import { Grid2, Typography } from "@mui/material";
import { useEffect, useState } from "react";

import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { useAssetId } from "@/setup/hooks/useAssetId";
import { DEFAULT_MILL, type MillId } from "@/setup/mill-config/mill-config";
import { routeProductsTestShackSheet } from "@/setup/routes/products.routes";
import { pageBgColor } from "@/utils/theme";
import type { ShiftInspection } from "../models/Inspection";
import { useShiftInspectionsQuery } from "../hooks/queries/useShiftInspectionsQuery";
import { useTestShackReportFiltersQuery } from "../hooks/queries/useTestShackReportFiltersQuery";
import type { TestShackReportFilterOptions } from "../models/TestShackReport";
import { useDeleteInspectionMutation } from "../hooks/mutations/useInspectionMutations";
import { TestShackInspectionsTable } from "./components/TestShackInspectionsTable";
import { TestShackManagerFilters } from "./components/TestShackManagerFilters";
import { useTestShackManagerUrlState } from "./hooks/useTestShackManagerUrlState";

const EMPTY_FILTER_OPTIONS: TestShackReportFilterOptions = {
	sections: [],
	events: [],
	heats: [],
};

interface TestShackManagerPageProps {
	/** Fixed mill for mill-specific routes. Without it the user's area is used. */
	assetId?: MillId;
	sheetRoute?: string;
}

export const TestShackManagerPage = ({
	assetId: fixedAssetId,
	sheetRoute = routeProductsTestShackSheet,
}: TestShackManagerPageProps = {}) => {
	const userAssetId = useAssetId() as MillId;
	const assetId = fixedAssetId ?? userAssetId ?? DEFAULT_MILL.id;
	return (
		<TestShackManagerPageContent
			key={assetId}
			assetId={assetId}
			sheetRoute={sheetRoute}
		/>
	);
};

const TestShackManagerPageContent = ({
	assetId,
	sheetRoute,
}: {
	assetId: MillId;
	sheetRoute: string;
}) => {
	const { filters, updateFilters } = useTestShackManagerUrlState();
	const { shiftInfo, getShiftDataFromAPI } = useShiftNavigator();

	const [error, setError] = useState("");
	const [toDelete, setToDelete] = useState<ShiftInspection | null>(null);
	const [highlight, setHighlight] = useState<{
		profileInspectionId: number;
		shiftId: number;
	} | null>(() =>
		filters.profileInspectionId !== null && filters.shiftId !== null
			? {
					profileInspectionId: filters.profileInspectionId,
					shiftId: filters.shiftId,
				}
			: null,
	);

	const isShiftMode = filters.mode === "shift";
	const shiftId = isShiftMode
		? (shiftInfo?.CurrentShiftId ?? filters.shiftId)
		: null;
	const startDate = isShiftMode ? null : filters.startDate.toDate();
	const endDate = isShiftMode ? null : filters.endDate.toDate();
	const isRangeReady = !isShiftMode || shiftId !== null;
	const highlightedProfileInspectionId =
		highlight !== null && highlight.shiftId === shiftId
			? highlight.profileInspectionId
			: null;

	const { data: filtersData, isLoading: filterIsLoading } =
		useTestShackReportFiltersQuery({
			assetId,
			startDate,
			endDate,
			shiftId,
			sectionId: filters.sectionId,
			eventId: null,
			enabled: isRangeReady,
			onError: (e: Error) => setError(e.message),
		});
	const options = filtersData;
	const hasStaleSection =
		filters.sectionId !== null &&
		options !== undefined &&
		!options.sections.some(
			(section) => section.SectionId === filters.sectionId,
		);
	const hasStaleHeat =
		filters.heatId !== null &&
		options !== undefined &&
		!options.heats.some((heat) => heat.HeatId === filters.heatId);
	const hasStaleSelection = hasStaleSection || hasStaleHeat;

	const {
		data,
		isInitialLoading: isInspectionsInitialLoading,
		isError,
		error: e,
	} = useShiftInspectionsQuery({
		assetId,
		shiftId,
		startDate,
		endDate,
		sectionId: filters.sectionId,
		heatId: filters.heatId,
		enabled: isRangeReady && options !== undefined && !hasStaleSelection,
	});

	const deleteMutation = useDeleteInspectionMutation({
		onError: (deleteError: Error) => setError(deleteError.message),
	});

	useEffect(() => {
		if (isError && e) {
			setError(e.message);
		}
	}, [isError, e]);

	useEffect(() => {
		if (isShiftMode) {
			getShiftDataFromAPI(null, filters.shiftId ?? 0, assetId);
		}
	}, [isShiftMode]);

	useEffect(() => {
		if (isShiftMode && shiftInfo?.CurrentShiftId) {
			updateFilters({ shiftId: shiftInfo.CurrentShiftId });
		}
	}, [isShiftMode, shiftInfo]);

	useEffect(() => {
		if (highlight === null || shiftId === null) return;
		if (highlight.shiftId === shiftId) return;

		setHighlight(null);
		updateFilters({ profileInspectionId: null });
	}, [highlight, shiftId]);

	useEffect(() => {
		if (hasStaleSection) {
			updateFilters({ sectionId: null, heatId: null });
			return;
		}
		if (hasStaleHeat) void updateFilters({ heatId: null });
	}, [hasStaleHeat, hasStaleSection]);

	return (
		<>
			<HelmetDexteel title="Test Shack Manager" />
			<Grid2
				container
				size={{ md: 12, xs: 12 }}
				spacing={1}
				px={2}
				pt={2}
				sx={{ backgroundColor: pageBgColor, minHeight: "calc(100vh - 160px)" }}
			>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" fontWeight={600}>
						Test Shack Manager
					</Typography>
				</Grid2>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<TestShackManagerFilters
						assetId={assetId}
						filters={filters}
						options={filtersData ?? EMPTY_FILTER_OPTIONS}
						isLoadingOptions={filterIsLoading}
						onChange={(updates) => void updateFilters(updates)}
					/>
				</Grid2>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<TestShackInspectionsTable
						sheetRoute={sheetRoute}
						inspections={data?.inspections ?? []}
						isLoading={filterIsLoading || isInspectionsInitialLoading}
						highlightedProfileInspectionId={highlightedProfileInspectionId}
						onDelete={setToDelete}
					/>
				</Grid2>
			</Grid2>
			<ConfirmationDialogRaw
				open={toDelete !== null}
				title="Delete inspection"
				message={
					toDelete
						? `Delete the sample of heat ${toDelete.HeatName ?? "-"} taken on ${dxtToLocalServerTime(
								toDelete.InspectedAt,
								"dd MMM yyyy HH:mm",
							)}?`
						: ""
				}
				buttons={{ no: "Cancel", yes: "Delete" }}
				onClose={(value?: string) => {
					if (value === "OK" && toDelete)
						deleteMutation.mutate(toDelete.ProfileInspectionId);
					setToDelete(null);
				}}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
